import type { AdminEmailDailyChartProps } from "../../models/admin-email.models";

// Graph exact mailbox totals for the selected UTC dates, independently of message pagination.
// A missing provider count gets a visible question mark instead of a zero-height bar claiming no mail arrived.
const AdminEmailDailyChart = ({ response }: AdminEmailDailyChartProps) => {
  const maximum = Math.max(1, ...response.dailyCounts.map((day) => day.receivedEmails ?? 0));
  const knownTotal = response.dailyCounts.reduce((total, day) => total + (day.receivedEmails ?? 0), 0);

  return (
    <figure data-test="admin-email-daily-chart" className="mt-4">
      <figcaption className="flex flex-wrap items-center justify-between gap-2 text-xs opacity-70">
        <span>
          {response.from} – {response.to} · UTC
        </span>
        <span>
          {response.complete ? "Total received" : "Known received"}: {knownTotal.toLocaleString()}
        </span>
      </figcaption>
      {!response.complete && (
        <p
          role="status"
          data-test="admin-email-daily-chart-incomplete"
          className="mt-3 rounded bg-[#FFF8E1] p-3 text-xs text-[#946200]"
        >
          Some daily counts are unavailable. The chart and known total are incomplete.
        </p>
      )}
      <div className="mt-4 overflow-x-auto pb-2">
        <div className="flex h-32 min-w-[360px] items-end gap-1.5" aria-label="Received emails per UTC day">
          {response.dailyCounts.map((day) => {
            const unavailable = day.receivedEmails === null;
            const label = `${day.date}: ${unavailable ? "count unavailable" : `${day.receivedEmails} emails`}`;

            return (
              <div
                key={day.date}
                role="img"
                tabIndex={0}
                title={label}
                aria-label={label}
                data-test={`admin-email-daily-count-${day.date}`}
                className="relative flex h-full min-w-[8px] flex-1 items-end rounded-t bg-[#E9F4F3] focus-visible:outline focus-visible:outline-2 focus-visible:outline-green dark:bg-gray-700"
              >
                {unavailable ? (
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-[#946200]">
                    ?
                  </span>
                ) : (
                  <span
                    className="w-full rounded-t bg-green dark:bg-teal-400"
                    style={{ height: `${((day.receivedEmails ?? 0) / maximum) * 100}%` }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-1 flex justify-between text-[10px] opacity-60" aria-hidden="true">
        <span>{response.from}</span>
        <span>{response.to}</span>
      </div>
      {response.errors && response.errors.length > 0 && (
        <details className="mt-3 text-xs" data-test="admin-email-daily-count-errors">
          <summary className="cursor-pointer font-semibold">Unavailable days ({response.errors.length})</summary>
          <ul className="mt-2 space-y-1">
            {response.errors.map((error) => (
              <li key={error.date}>
                {error.date}: {error.message}
              </li>
            ))}
          </ul>
        </details>
      )}
    </figure>
  );
};

export default AdminEmailDailyChart;
